import React, { useEffect, useMemo, useRef, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { Checkbox, FormControlLabel, Menu, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { ButtonVariant } from "Constant/button";

import StandartButton from "components/atoms/StandartButton";

import {
    CardActions,
    CardExpandToggle,
    CardField,
    CardFields,
    CardLabel,
    CardList,
    CardValue,
    ColumnMenuHeader,
    ColumnMenuList,
    EmptyState,
    ExpandCell,
    ExpandedContent,
    ExpandedRowCell,
    ExpandToggle,
    RowCard,
    StyledTable,
    TableCard,
    TableRow,
    TableScroll,
    TableToolbar,
    Tbody,
    Td,
    Th,
    Thead,
    ToolbarActions,
    ToolbarEnd,
} from "./GenericTable.styled";
import { GenericTableColumn, GenericTableProps } from "./GenericTable.types";
import {
    readHiddenColumns,
    writeHiddenColumns,
} from "./genericTableColumnStorage";
import TablePagination from "./TablePagination";
import TableToolbarActionButton from "./TableToolbarActionButton";

const getColumnLabel = (column: GenericTableColumn) => {
    if (column.label) return column.label;
    if (typeof column.header === "string" && column.header.trim()) {
        return column.header;
    }
    return column.key;
};

const isCardActionsColumn = (column: GenericTableColumn) =>
    column.cardActions === true ||
    (column.cardActions !== false && column.key === "actions");

// Fixed width of the leading chevron column rendered when rows are expandable.
const EXPAND_COLUMN_WIDTH = 44;

const GenericTable = ({
    columns,
    rows,
    getRowKey,
    emptyState,
    minWidth,
    pageSize,
    onPageSizeChange,
    pageSizeOptions,
    fillHeight,
    currentPage: serverCurrentPage,
    totalPages: serverTotalPages,
    totalItems: serverTotalItems,
    onPageChange,
    loading,
    columnToggle,
    tableName,
    defaultHiddenColumns = [],
    cardView = true,
    onRowClick,
    isRowSelected,
    selectedRowVariant = "primary",
    autoScroll = true,
    ellipsis = true,
    actionButtons,
    renderExpanded,
    expandOnRowClick = true,
}: GenericTableProps) => {
    const theme = useTheme();
    const selectedRowColor =
        selectedRowVariant === "black"
            ? alpha(theme.palette.common.black, 0.08)
            : alpha(theme.palette.primary.main, 0.12);
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
    const useCards = cardView && isMobileOrTablet;
    const isServerPaginated = !!onPageChange;
    const isExpandable = !!renderExpanded;
    const [page, setPage] = useState(1);
    const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(
        () => new Set(),
    );

    const toggleExpanded = (key: string | number) => {
        setExpandedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };
    const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => {
        const fromColumns = columns
            .filter((column) => column.defaultHidden)
            .map((column) => column.key);
        const defaults = new Set([...fromColumns, ...defaultHiddenColumns]);
        if (!tableName) return defaults;

        const stored = readHiddenColumns(tableName);
        if (!stored) return defaults;

        const validKeys = new Set(columns.map((column) => column.key));
        const hideableKeys = new Set(
            columns
                .filter((column) => column.hideable !== false)
                .map((column) => column.key),
        );
        const next = new Set(
            stored.filter((key) => validKeys.has(key) && hideableKeys.has(key)),
        );
        const remainingVisible = columns.filter(
            (column) => !next.has(column.key),
        );
        if (remainingVisible.length === 0) return defaults;
        return next;
    });
    const [columnMenuAnchor, setColumnMenuAnchor] =
        useState<null | HTMLElement>(null);
    const rowCount = rows?.length ?? 0;
    const tableScrollRef = useRef<HTMLDivElement>(null);
    const isFirstPageRender = useRef(true);
    const activePage = isServerPaginated ? (serverCurrentPage ?? 1) : page;

    const visibleColumns = useMemo(
        () => columns.filter((column) => !hiddenKeys.has(column.key)),
        [columns, hiddenKeys],
    );

    const fieldColumns = useMemo(
        () =>
            visibleColumns.filter(
                (column) =>
                    !isCardActionsColumn(column) &&
                    !(useCards && column.hideInCardView),
            ),
        [visibleColumns, useCards],
    );
    const actionColumns = useMemo(
        () =>
            visibleColumns.filter(
                (column) =>
                    isCardActionsColumn(column) &&
                    !(useCards && column.hideInCardView),
            ),
        [visibleColumns, useCards],
    );

    useEffect(() => {
        if (!isServerPaginated) setPage(1);
    }, [rowCount, pageSize, isServerPaginated]);

    useEffect(() => {
        if (isFirstPageRender.current) {
            isFirstPageRender.current = false;
            return;
        }
        if (!autoScroll) return;
        const node = tableScrollRef.current;
        if (!node) return;

        node.scrollTo({ top: 0, behavior: "smooth" });

        const targetTop = node.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: targetTop, behavior: "smooth" });
    }, [activePage, autoScroll]);

    const toggleColumn = (key: string, hideable: boolean) => {
        if (!hideable) return;

        setHiddenKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
                if (tableName) writeHiddenColumns(tableName, next);
                return next;
            }

            const remainingVisible = columns.filter(
                (column) => column.key !== key && !next.has(column.key),
            );
            if (remainingVisible.length === 0) return prev;

            next.add(key);
            if (tableName) writeHiddenColumns(tableName, next);
            return next;
        });
    };

    const flexibleColumns = visibleColumns.filter(
        (column) => column.fixedWidth == null,
    );
    const hasColumnWidths = flexibleColumns.some(
        (column) => column.width != null,
    );
    const widthTotal = hasColumnWidths
        ? flexibleColumns.reduce((sum, column) => sum + (column.width ?? 1), 0)
        : 0;
    const fixedWidthTotal = visibleColumns.reduce(
        (sum, column) => sum + (column.fixedWidth ?? 0),
        0,
    );
    // Browsers often ignore calc()/%% mixes on <table> columns and share
    // leftover space evenly — measure the container and set pixel widths.
    const [containerWidth, setContainerWidth] = useState(0);
    useEffect(() => {
        if (useCards) return;
        const node = tableScrollRef.current;
        if (!node) return;

        const update = () => setContainerWidth(node.clientWidth);
        update();

        const observer = new ResizeObserver(update);
        observer.observe(node);
        return () => observer.disconnect();
    }, [useCards]);

    const getColumnWidth = (column: GenericTableColumn) => {
        if (column.fixedWidth != null) return undefined;
        if (!hasColumnWidths || widthTotal <= 0) return undefined;
        const ratio = (column.width ?? 1) / widthTotal;
        if (containerWidth > 0) {
            // Reserve space for fixed-width columns and the expand chevron
            // column; otherwise ratio columns overflow the table by that width.
            const reserved =
                fixedWidthTotal + (isExpandable ? EXPAND_COLUMN_WIDTH : 0);
            const flexiblePx = Math.max(0, containerWidth - reserved);
            return `${flexiblePx * ratio}px`;
        }
        // Before measure: fall back to % of full table (approx).
        return `${ratio * 100}%`;
    };
    // Horizontal scroll only when the caller explicitly sets table minWidth.
    // fixedWidth + ratio columns always fit within 100% (no overflow-x).
    const tableMinWidth = minWidth;
    const enableOverflowX = minWidth != null;
    const useFixedLayout = hasColumnWidths || fixedWidthTotal > 0;

    const hasActionButtons =
        !useCards && Array.isArray(actionButtons) && actionButtons.length > 0;
    const showToolbar = columnToggle || hasActionButtons;

    const columnToggleButton = columnToggle ? (
        <>
            <StandartButton
                variant={ButtonVariant.BLACK_OUTLINED}
                size="small"
                startIcon={<ViewColumnIcon />}
                aria-label="Columns"
                aria-expanded={Boolean(columnMenuAnchor)}
                aria-haspopup="menu"
                onClick={(event) => setColumnMenuAnchor(event.currentTarget)}
                sx={{
                    height: 32,
                    minHeight: 32,
                    py: 0,
                    lineHeight: 1.2,
                    "& .MuiButton-startIcon": {
                        marginRight: "6px",
                        "& > *:nth-of-type(1)": {
                            fontSize: 18,
                        },
                    },
                }}
            >
                Columns
            </StandartButton>
            <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={() => setColumnMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        mt: 0.75,
                        borderRadius: "8px",
                        border: "1px solid #e5e5ea",
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
                    },
                }}
            >
                <ColumnMenuHeader>Visible columns</ColumnMenuHeader>
                <ColumnMenuList>
                    {columns.map((column) => {
                        const hideable = column.hideable !== false;
                        const checked = !hiddenKeys.has(column.key);
                        return (
                            <FormControlLabel
                                key={column.key}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={checked}
                                        disabled={!hideable}
                                        onChange={() =>
                                            toggleColumn(column.key, hideable)
                                        }
                                        sx={{
                                            color: "#c7c7cc",
                                            "&.Mui-checked": {
                                                color: "#f66700",
                                            },
                                        }}
                                    />
                                }
                                label={getColumnLabel(column)}
                                sx={{
                                    display: "flex",
                                    mr: 0,
                                    width: "100%",
                                    ml: 0,
                                    borderRadius: "6px",
                                    px: 0.5,
                                    "&:hover": {
                                        background: "#fafafa",
                                    },
                                    "& .MuiFormControlLabel-label": {
                                        fontFamily: '"Nunito", sans-serif',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: hideable ? "#3a3a3c" : "#86868b",
                                    },
                                }}
                            />
                        );
                    })}
                </ColumnMenuList>
            </Menu>
        </>
    ) : null;

    const toolbar = showToolbar ? (
        <TableToolbar>
            <ToolbarActions>
                {hasActionButtons
                    ? actionButtons!.map((action, index) => (
                          <TableToolbarActionButton
                              key={action.key || `${action.name}-${index}`}
                              action={action}
                          />
                      ))
                    : null}
            </ToolbarActions>
            {columnToggleButton ? (
                <ToolbarEnd>{columnToggleButton}</ToolbarEnd>
            ) : null}
        </TableToolbar>
    ) : null;

    if (!rowCount) {
        return (
            <TableCard $fillHeight={fillHeight} $flush={useCards}>
                {toolbar}
                <EmptyState $fillHeight={fillHeight}>
                    {emptyState ?? "No data found."}
                </EmptyState>
            </TableCard>
        );
    }

    const totalPages = isServerPaginated
        ? Math.max(1, serverTotalPages ?? 1)
        : pageSize
          ? Math.max(1, Math.ceil(rowCount / pageSize))
          : 1;
    const currentPage = isServerPaginated
        ? (serverCurrentPage ?? 1)
        : Math.min(page, totalPages);
    const totalItems = isServerPaginated
        ? (serverTotalItems ?? rowCount)
        : rowCount;
    const pagedRows = isServerPaginated
        ? rows
        : pageSize
          ? rows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
          : rows;

    // Remount body/cards when visible row identity changes (after fetch),
    // not merely when currentPage flips — otherwise the fade plays on stale
    // rows and the new payload snaps in with no animation.
    const contentKey = pagedRows
        .map((row, index) => String(getRowKey(row, index)))
        .join("|");

    const pagination =
        pageSize &&
        (isServerPaginated || rowCount > pageSize || onPageSizeChange) ? (
            <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={isServerPaginated ? onPageChange! : setPage}
                onPageSizeChange={onPageSizeChange}
                pageSizeOptions={pageSizeOptions}
                loading={loading}
            />
        ) : null;

    return (
        <TableCard $fillHeight={fillHeight} $flush={useCards}>
            {toolbar}
            <TableScroll
                $fillHeight={fillHeight}
                $overflowX={enableOverflowX && !useCards}
                ref={tableScrollRef}
            >
                {useCards ? (
                    <CardList key={contentKey}>
                        {pagedRows.map((row, index) => {
                            const rowKey = getRowKey(row, index);
                            const isSelected = isRowSelected?.(row, index);
                            const expandedContent = renderExpanded?.(
                                row,
                                index,
                            );
                            const isOpen = expandedKeys.has(rowKey);
                            return (
                                <RowCard
                                    key={rowKey}
                                    style={{
                                        cursor: onRowClick
                                            ? "pointer"
                                            : undefined,
                                        backgroundColor: isSelected
                                            ? selectedRowColor
                                            : undefined,
                                    }}
                                    onClick={
                                        onRowClick
                                            ? () => onRowClick(row, index)
                                            : undefined
                                    }
                                    onKeyDown={
                                        onRowClick
                                            ? (
                                                  event: React.KeyboardEvent<HTMLElement>,
                                              ) => {
                                                  if (
                                                      event.key === "Enter" ||
                                                      event.key === " "
                                                  ) {
                                                      event.preventDefault();
                                                      onRowClick(row, index);
                                                  }
                                              }
                                            : undefined
                                    }
                                    role={onRowClick ? "button" : undefined}
                                    tabIndex={onRowClick ? 0 : undefined}
                                >
                                    <CardFields>
                                        {fieldColumns.map((column) => (
                                            <CardField key={column.key}>
                                                <CardLabel>
                                                    {getColumnLabel(column)}
                                                </CardLabel>
                                                <CardValue $ellipsis={ellipsis}>
                                                    {column.render(row, index)}
                                                </CardValue>
                                            </CardField>
                                        ))}
                                    </CardFields>
                                    {actionColumns.length > 0 && (
                                        <CardActions>
                                            {actionColumns.map((column) => (
                                                <React.Fragment
                                                    key={column.key}
                                                >
                                                    {column.render(row, index)}
                                                </React.Fragment>
                                            ))}
                                        </CardActions>
                                    )}
                                    {isExpandable && expandedContent ? (
                                        <>
                                            <CardExpandToggle
                                                type="button"
                                                onClick={() =>
                                                    toggleExpanded(rowKey)
                                                }
                                            >
                                                {isOpen ? "Hide" : "View"}{" "}
                                                details
                                                {isOpen ? (
                                                    <KeyboardArrowDownIcon
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                ) : (
                                                    <KeyboardArrowRightIcon
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                )}
                                            </CardExpandToggle>
                                            {isOpen && (
                                                <ExpandedContent>
                                                    {expandedContent}
                                                </ExpandedContent>
                                            )}
                                        </>
                                    ) : null}
                                </RowCard>
                            );
                        })}
                    </CardList>
                ) : (
                    <StyledTable
                        $minWidth={tableMinWidth}
                        $fixedLayout={useFixedLayout}
                    >
                        {useFixedLayout && (
                            <colgroup>
                                {isExpandable && (
                                    <col
                                        style={{
                                            width: `${EXPAND_COLUMN_WIDTH}px`,
                                        }}
                                    />
                                )}
                                {visibleColumns.map((column) => {
                                    const ratioWidth = getColumnWidth(column);
                                    return (
                                        <col
                                            key={column.key}
                                            style={
                                                column.fixedWidth != null
                                                    ? {
                                                          width: `${column.fixedWidth}px`,
                                                      }
                                                    : ratioWidth
                                                      ? { width: ratioWidth }
                                                      : undefined
                                            }
                                        />
                                    );
                                })}
                            </colgroup>
                        )}
                        <Thead>
                            <tr>
                                {isExpandable && (
                                    <Th $fixedWidth={EXPAND_COLUMN_WIDTH}>
                                        <span />
                                    </Th>
                                )}
                                {visibleColumns.map((column) => (
                                    <Th
                                        key={column.key}
                                        $align={column.align}
                                        $width={getColumnWidth(column)}
                                        $minWidth={column.minWidth}
                                        $fixedWidth={column.fixedWidth}
                                        $ellipsis={ellipsis}
                                    >
                                        {column.header}
                                    </Th>
                                ))}
                            </tr>
                        </Thead>
                        <Tbody key={contentKey}>
                            {pagedRows.map((row, index) => {
                                const rowKey = getRowKey(row, index);
                                const isSelected = isRowSelected?.(row, index);
                                const expandedContent = renderExpanded?.(
                                    row,
                                    index,
                                );
                                const canExpand =
                                    isExpandable && !!expandedContent;
                                const isOpen = expandedKeys.has(rowKey);
                                return (
                                    <React.Fragment key={rowKey}>
                                        <TableRow
                                            style={{
                                                cursor:
                                                    onRowClick ||
                                                    (canExpand &&
                                                        expandOnRowClick)
                                                        ? "pointer"
                                                        : undefined,
                                            }}
                                            onClick={
                                                onRowClick ||
                                                (canExpand && expandOnRowClick)
                                                    ? () => {
                                                          onRowClick?.(
                                                              row,
                                                              index,
                                                          );
                                                          if (
                                                              canExpand &&
                                                              expandOnRowClick
                                                          ) {
                                                              toggleExpanded(
                                                                  rowKey,
                                                              );
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            onKeyDown={
                                                onRowClick ||
                                                (canExpand && expandOnRowClick)
                                                    ? (
                                                          event: React.KeyboardEvent<HTMLTableRowElement>,
                                                      ) => {
                                                          if (
                                                              event.key !==
                                                                  "Enter" &&
                                                              event.key !== " "
                                                          ) {
                                                              return;
                                                          }
                                                          event.preventDefault();
                                                          onRowClick?.(
                                                              row,
                                                              index,
                                                          );
                                                          if (
                                                              canExpand &&
                                                              expandOnRowClick
                                                          ) {
                                                              toggleExpanded(
                                                                  rowKey,
                                                              );
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            role={
                                                onRowClick ||
                                                (canExpand && expandOnRowClick)
                                                    ? "button"
                                                    : undefined
                                            }
                                            tabIndex={
                                                onRowClick ||
                                                (canExpand && expandOnRowClick)
                                                    ? 0
                                                    : undefined
                                            }
                                        >
                                            {isExpandable && (
                                                <ExpandCell>
                                                    <ExpandToggle
                                                        type="button"
                                                        disabled={!canExpand}
                                                        aria-label={
                                                            isOpen
                                                                ? "Collapse row"
                                                                : "Expand row"
                                                        }
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            toggleExpanded(
                                                                rowKey,
                                                            );
                                                        }}
                                                    >
                                                        {isOpen ? (
                                                            <KeyboardArrowDownIcon
                                                                sx={{
                                                                    fontSize: 18,
                                                                }}
                                                            />
                                                        ) : (
                                                            <KeyboardArrowRightIcon
                                                                sx={{
                                                                    fontSize: 18,
                                                                }}
                                                            />
                                                        )}
                                                    </ExpandToggle>
                                                </ExpandCell>
                                            )}
                                            {visibleColumns.map((column) => (
                                                <Td
                                                    key={column.key}
                                                    $align={column.align}
                                                    $width={getColumnWidth(
                                                        column,
                                                    )}
                                                    $minWidth={column.minWidth}
                                                    $fixedWidth={
                                                        column.fixedWidth
                                                    }
                                                    $ellipsis={ellipsis}
                                                    style={{
                                                        backgroundColor:
                                                            isSelected
                                                                ? selectedRowColor
                                                                : undefined,
                                                    }}
                                                >
                                                    {column.render(row, index)}
                                                </Td>
                                            ))}
                                        </TableRow>
                                        {canExpand && isOpen && (
                                            <tr>
                                                <ExpandedRowCell
                                                    colSpan={
                                                        visibleColumns.length +
                                                        1
                                                    }
                                                >
                                                    <ExpandedContent>
                                                        {expandedContent}
                                                    </ExpandedContent>
                                                </ExpandedRowCell>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </Tbody>
                    </StyledTable>
                )}
            </TableScroll>

            {pagination}
        </TableCard>
    );
};

export default GenericTable;
